import { useMemo } from 'react';
import { LineChart } from '@mantine/charts';
import { Card, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { CHART_AXIS_PROPS } from '@/components/charts';
import { useFormat } from '@/components/useFormat';
import type { Profile } from '@/planner/types';
import { MAP } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { profileRows } from './chartData';

const LOW_CLEARANCE_M = 30;
const HIGH_CLEARANCE_M = 120;

export interface ProfileChartProps {
  profile: Profile;
  onHover: (distance_m: number | null) => void;
}

/** Height above takeoff and above ground along the path, with the 30 m and 120 m clearance limits. */
export function ProfileChart({ profile, onHover }: ProfileChartProps) {
  const { t } = useTranslation('missions');
  const { number } = useFormat();
  const rows = useMemo(() => profileRows(profile), [profile]);
  const hasTerrain = profile.terrain_m !== null;
  const series = [
    { name: 'relative', color: MAP.flightLine, label: t('charts.relative') },
    ...(hasTerrain ? [{ name: 'clearance', color: MAP.missionWaypoint, label: t('charts.clearance') }] : []),
  ];
  return (
    <Card>
      <Stack gap="xs">
        <Text fw={600}>{t('charts.profile')}</Text>
        <Text fz={12} c={brand.textMuted}>
          {t('charts.height')}
        </Text>
        <LineChart
          h={440}
          data={rows}
          dataKey="d"
          series={series}
          withDots={false}
          connectNulls={false}
          curveType="linear"
          xAxisLabel={t('charts.distance')}
          referenceLines={[
            { y: LOW_CLEARANCE_M, label: t('charts.limitLow'), color: MAP.takeoff },
            { y: HIGH_CLEARANCE_M, label: t('charts.limitHigh'), color: MAP.takeoff },
          ]}
          valueFormatter={(v) => number(v)}
          withLegend
          legendProps={{ verticalAlign: 'bottom' }}
          lineChartProps={{
            onMouseMove: (state: unknown) => {
              const v = (state as { activeLabel?: unknown } | undefined)?.activeLabel;
              onHover(typeof v === 'number' ? v : v == null ? null : Number(v));
            },
            onMouseLeave: () => onHover(null),
          }}
          {...CHART_AXIS_PROPS}
        />
      </Stack>
    </Card>
  );
}
